import React, { useState, useEffect, useRef, useMemo } from 'react';
import { 
  ClipboardList, Plus, X, Search, CheckCircle, 
  AlertTriangle, Trash2, Eye, FileText, Lock, Barcode, ShieldAlert, Info, ArrowUpDown, History, Inbox
} from 'lucide-react';
import Sidebar from '../../components/Sidebar';
import API, { socket } from '../../services/api';

const InventaireMp = () => {
  const [inventaires, setInventaires] = useState([]);
  const [selectedInventaire, setSelectedInventaire] = useState(null);
  const [items, setItems] = useState([]);
  const [allMatieres, setAllMatieres] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [libelle, setLibelle] = useState('');
  
  const [mainTab, setMainTab] = useState('actifs');
  const [selectedSessionId, setSelectedSessionId] = useState(null);
  const [sessionDetails, setSessionDetails] = useState([]);

  // Filtres
  const [searchNom, setSearchNom] = useState('');
  const [searchCode, setSearchCode] = useState('');
  const [searchCategorie, setSearchCategorie] = useState('');

  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  const [articleEnCours, setArticleEnCours] = useState(null);
  const [qteSaisie, setQteSaisie] = useState('');
  const inputRef = useRef(null);

  const [dialogue, setDialogue] = useState({ show: false, titre: '', msg: '', type: 'primary', action: null });

  const [alert, setAlert] = useState({
    show: false, type: 'info', message: '', onConfirm: null, color: '#059669'
  });

  const primaryColor = '#059669';
  const borderColor = '#e2e8f0';

  useEffect(() => {
    fetchInventaires();
    fetchMatieresRef();

    if (socket) {
      socket.on('REFRESH_UI', (data) => {
        if (data?.module === 'INVENTORY_MP' || data?.module === 'STOCK_MP') {
          fetchInventaires();
          if (selectedInventaire) fetchDetails(selectedInventaire.id);
        }
      });
    }

    return () => {
      if (socket) socket.off('REFRESH_UI');
    };
  }, [selectedInventaire]);

  const fetchInventaires = async () => {
    try {
      const res = await API.get('/inventories-mp').catch(() => ({ data: [] }));
      setInventaires(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      showAlert('error', 'Impossible de charger les inventaires MP.', '#ef4444');
    }
  };

  const fetchMatieresRef = async () => {
    try {
      const res = await API.get('/raw-materials');
      setAllMatieres(Array.isArray(res.data) ? res.data : (res.data.matieres || []));
    } catch (err) {
      console.error("Erreur chargement matières premières de référence", err);
    }
  };

  const fetchDetails = async (id) => {
    try {
      const res = await API.get(`/inventories-mp/${id}`);
      setSelectedInventaire(res.data.inventaire);
      setItems(res.data.items || []);
    } catch (err) {
      showAlert('error', 'Erreur lors du chargement des détails.', '#ef4444');
    }
  };

  const handleSelectSessionHistorique = async (id) => {
    if (selectedSessionId === id) {
      setSelectedSessionId(null);
      setSessionDetails([]);
      return;
    }
    try {
      setSelectedSessionId(id);
      const res = await API.get(`/inventories-mp/${id}`);
      setSessionDetails(res.data.items || []);
    } catch (err) {
      showAlert('error', 'Erreur lors du chargement des détails de la session.', '#ef4444');
    }
  };

  const showAlert = (type, message, color = '#059669') => {
    setAlert({ show: true, type, message, color });
    if (type !== 'confirm') {
      setTimeout(() => setAlert(prev => ({ ...prev, show: false })), 4000);
    }
  };

  const verifierEtOuvrirModal = async () => {
    try {
      const res = await API.get('/verification-magasin/stock-attente');
      const enAttente = res.data?.nombre || res.data?.count || 0;
      
      if (enAttente > 0) {
        showAlert('error', `Impossible de lancer l'inventaire : il y a ${enAttente} stock(s) en attente de régularisation.`, '#ef4444');
        return;
      }
    } catch (err) {
      console.warn("Vérification stock attente ignorée ou indisponible");
    }

    const codeAuto = `INV-MP-${Date.now().toString().slice(-6)}`;
    setLibelle(`INVENTAIRE MP - ${codeAuto}`);
    setIsModalOpen(true);
  };

  const handleCreateInventaire = async (e) => {
    e.preventDefault();
    if (!libelle.trim()) return;

    try {
      const res = await API.post('/inventories-mp', { libelle: libelle.toUpperCase() });
      setIsModalOpen(false);
      setLibelle('');
      fetchInventaires();
      showAlert('success', 'Inventaire MP initialisé avec succès !', primaryColor);
      if (res.data?.id) fetchDetails(res.data.id);
    } catch (err) {
      showAlert('error', err.response?.data?.error || "Échec de la création de l'inventaire.", '#ef4444');
    }
  };

  const selectionnerArticle = (item) => {
    if (selectedInventaire?.statut === 'valide') return;
    setArticleEnCours(item);
    const isCompté = item.stock_reel !== undefined && item.stock_reel !== null && item.stock_reel !== '';
    setQteSaisie(isCompté ? item.stock_reel : '');
    setTimeout(() => inputRef.current?.focus(), 100);
  };

  const enregistrerQte = async () => {
    if (!articleEnCours || qteSaisie === '') return;
    const qte = parseFloat(qteSaisie) || 0;

    try {
      await API.put(`/inventories-mp/items/${articleEnCours.id}`, { stock_reel: qte });
      
      setItems(items.map(i => i.id === articleEnCours.id ? { 
        ...i, 
        stock_reel: qte, 
        ecart_valeur: (qte - i.stock_theorique) * i.prix_achat_snap
      } : i));

      setArticleEnCours(null);
      setQteSaisie('');
    } catch (err) {
      showAlert('error', err.response?.data?.error || "Erreur lors de la mise à jour du stock réel.", '#ef4444');
    }
  };

  const mettreAZeroLeReste = async () => {
    if (!selectedInventaire || selectedInventaire.statut === 'valide') return;

    const itemsNonSaisis = items.filter(i => i.stock_reel === undefined || i.stock_reel === null || i.stock_reel === '');
    if (itemsNonSaisis.length === 0) {
      showAlert('info', 'Tous les articles ont déjà une quantité saisie.', primaryColor);
      return;
    }

    try {
      await Promise.all(itemsNonSaisis.map(i => 
        API.put(`/inventories-mp/items/${i.id}`, { stock_reel: 0 })
      ));

      fetchDetails(selectedInventaire.id);
      showAlert('success', 'Les articles non saisis ont été mis à 0 avec succès.', primaryColor);
    } catch (err) {
      showAlert('error', "Erreur lors de la mise à zéro des articles restants.", '#ef4444');
    }
  };

  const handleValidateInventaire = (invId) => {
    ouvrirDialogue(
      "Clôturer l'inventaire ?",
      "Valider cet inventaire va impacter et rectifier les stocks réels de toutes les Matières Premières. Continuer ?",
      "primary",
      async () => {
        try {
          await API.post(`/inventories-mp/${invId}/validate`);
          fetchInventaires();
          setSelectedInventaire(null);
          setItems([]);
          setArticleEnCours(null);
          setMainTab('actifs');
          showAlert('success', 'Inventaire validé et appliqué au stock avec succès !', primaryColor);
        } catch (err) {
          showAlert('error', err.response?.data?.error || "Erreur lors de la validation.", '#ef4444');
        }
      }
    );
  };

  const supprimerInventaire = (invId) => {
    ouvrirDialogue(
      "Annuler et Supprimer ?",
      "Voulez-vous vraiment supprimer cet inventaire ?",
      "danger",
      async () => {
        try {
          await API.delete(`/inventories-mp/${invId}`);
          setSelectedInventaire(null);
          setItems([]);
          setArticleEnCours(null);
          fetchInventaires();
          showAlert('success', 'Inventaire supprimé avec succès.', primaryColor);
        } catch (err) {
          showAlert('error', "Erreur lors de la suppression.", '#ef4444');
        }
      }
    );
  };

  const annulerEtAbandonner = async () => {
    if (!selectedInventaire) return;
    try {
      await API.delete(`/inventories-mp/${selectedInventaire.id}`);
      setSelectedInventaire(null);
      setItems([]);
      setArticleEnCours(null);
      fetchInventaires();
      showAlert('success', 'Inventaire abandonné et supprimé.', primaryColor);
    } catch (err) {
      setSelectedInventaire(null);
      setItems([]);
      setArticleEnCours(null);
      fetchInventaires();
    }
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const totaux = useMemo(() => {
    return items.reduce((acc, item) => {
      const pa = parseFloat(item.prix_achat_snap || 0);
      const qTheo = parseFloat(item.stock_theorique || 0);
      const isCompté = item.stock_reel !== undefined && item.stock_reel !== null && item.stock_reel !== '';
      const qReel = isCompté ? parseFloat(item.stock_reel) : qTheo;
      
      acc.valTheo += qTheo * pa;
      acc.valReel += qReel * pa;
      acc.ecartVal += (qReel - qTheo) * pa;
      
      return acc;
    }, { valTheo: 0, valReel: 0, ecartVal: 0 });
  }, [items]);

  const inventairesActifs = useMemo(() => inventaires.filter(i => i.statut !== 'valide'), [inventaires]);
  const inventairesHistorique = useMemo(() => inventaires.filter(i => i.statut === 'valide'), [inventaires]);

  const applySortingAndFiltering = (sourceList) => {
    let result = sourceList.filter(item => {
      const matRef = allMatieres.find(m => m.id === item.matiere_id);
      const codeArt = matRef?.id || item.matiere_id || '';
      const nomArt = item.nom_matiere_snap || '';
      const categorieArt = matRef?.categorie || matRef?.categorie_nom || matRef?.category || '';

      const matchNom = nomArt.toLowerCase().includes(searchNom.toLowerCase());
      const matchCode = searchCode === '' || codeArt.toLowerCase().includes(searchCode.toLowerCase());
      const matchCat = searchCategorie === '' || categorieArt.toLowerCase().includes(searchCategorie.toLowerCase());

      return matchNom && matchCode && matchCat;
    });

    if (sortConfig.key) {
      result.sort((a, b) => {
        let aVal, bVal;
        const matA = allMatieres.find(m => m.id === a.matiere_id);
        const matB = allMatieres.find(m => m.id === b.matiere_id);

        if (sortConfig.key === 'id') {
          aVal = matA?.id || a.matiere_id;
          bVal = matB?.id || b.matiere_id;
        } else if (sortConfig.key === 'nom') {
          aVal = a.nom_matiere_snap;
          bVal = b.nom_matiere_snap;
        } else if (sortConfig.key === 'categorie') {
          aVal = matA?.categorie || '';
          bVal = matB?.categorie || '';
        } else if (sortConfig.key === 'stock_theorique') {
          aVal = Number(a.stock_theorique);
          bVal = Number(b.stock_theorique);
        } else if (sortConfig.key === 'stock_reel') {
          aVal = Number(a.stock_reel || 0);
          bVal = Number(b.stock_reel || 0);
        }

        if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }

    return result;
  };

  const itemsFiltrés = useMemo(() => applySortingAndFiltering(items), [items, searchNom, searchCode, searchCategorie, sortConfig, allMatieres]);
  const sessionDetailsFiltres = useMemo(() => applySortingAndFiltering(sessionDetails), [sessionDetails, searchNom, searchCode, searchCategorie, sortConfig, allMatieres]);

  const fermerDialogue = () => setDialogue({ ...dialogue, show: false });
  const ouvrirDialogue = (t, m, type, act) => setDialogue({ show: true, titre: t, msg: m, type, action: act });

  return (
    <div style={{ display: 'flex', height: '100vh', overflow: 'hidden', background: '#f1f5f9' }}>
      <Sidebar />

      {alert.show && (
        <div style={{ position: 'fixed', top: '20px', left: '50%', transform: 'translateX(-50%)', zIndex: 9999, display: 'flex', alignItems: 'center', gap: '15px', padding: '12px 24px', borderRadius: '12px', background: 'white', boxShadow: '0 10px 25px rgba(0,0,0,0.15)', borderLeft: `5px solid ${alert.color}` }}>
            <div style={{ color: alert.color }}>
                {alert.type === 'error' ? <X size={24}/> : <CheckCircle size={24}/>}
            </div>
            <div style={{ flex: 1 }}>
                <p style={{ margin: 0, fontWeight: '700', color: '#1e293b', fontSize: '14px' }}>{alert.message}</p>
            </div>
            <X size={18} style={{ cursor: 'pointer', color: '#94a3b8' }} onClick={() => setAlert({ ...alert, show: false })} />
        </div>
      )}

      <main style={{ flex: 1, display: 'flex', flexDirection: 'column', height: '100vh', position: 'relative', overflow: 'hidden', background: '#f8fafc' }}>
        
        <div style={{ flexShrink: 0, zIndex: 10, background: 'white', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)' }}>
            <header style={{ padding: '20px 40px', borderBottom: `1px solid ${borderColor}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <div style={{ background: '#d1fae5', color: primaryColor, padding: '10px', borderRadius: '10px', display: 'flex' }}>
                      <ClipboardList size={24} />
                  </div>
                  <div>
                      <h1 style={{ margin: 0, fontSize: '20px', fontWeight: '900' }}>
                          INVENTAIRE <span style={{color: primaryColor}}>MATIÈRES PREMIÈRES (MP)</span>
                      </h1>
                      <p style={{ margin: '2px 0 0 0', fontSize: '12px', color: '#64748b' }}>Comptage physique et régularisation des stocks de matières premières.</p>
                  </div>
              </div>
              
              {!selectedInventaire && mainTab === 'actifs' && (
                <button 
                  onClick={verifierEtOuvrirModal} 
                  style={{ background: primaryColor, color: 'white', padding: '10px 20px', borderRadius: '10px', border: 'none', cursor: 'pointer', fontWeight: '800', display: 'flex', alignItems: 'center', gap: '8px' }}
                >
                    <Plus size={18} /> NOUVEL INVENTAIRE MP
                </button>
              )}
            </header>
        </div>

        <div style={{ flex: 1, overflow: 'hidden', padding: '20px 40px', display: 'flex', flexDirection: 'column', boxSizing: 'border-box' }}>
          {!selectedInventaire ? (
            <div style={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' }}>
              
              <div style={{ display: 'flex', gap: '10px', marginBottom: '20px', flexShrink: 0 }}>
                <button 
                  onClick={() => { setMainTab('actifs'); setSelectedSessionId(null); setSearchCode(''); setSearchNom(''); setSearchCategorie(''); }}
                  style={{ padding: '10px 20px', borderRadius: '8px', border: 'none', fontWeight: '800', cursor: 'pointer', background: mainTab === 'actifs' ? primaryColor : '#e2e8f0', color: mainTab === 'actifs' ? 'white' : '#475569', display: 'flex', alignItems: 'center', gap: '8px' }}
                >
                  <Inbox size={16} /> Inventaires en cours ({inventairesActifs.length})
                </button>
                <button 
                  onClick={() => { setMainTab('historique'); setSelectedSessionId(null); setSearchCode(''); setSearchNom(''); setSearchCategorie(''); }}
                  style={{ padding: '10px 20px', borderRadius: '8px', border: 'none', fontWeight: '800', cursor: 'pointer', background: mainTab === 'historique' ? primaryColor : '#e2e8f0', color: mainTab === 'historique' ? 'white' : '#475569', display: 'flex', alignItems: 'center', gap: '8px' }}
                >
                  <History size={16} /> Historique des inventaires ({inventairesHistorique.length})
                </button>
              </div>

              {mainTab === 'actifs' && (
                <div style={{ background: 'white', borderRadius: '12px', border: `1px solid ${borderColor}`, overflowY: 'auto', maxHeight: 'calc(3 * 55px + 45px)' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead style={{ position: 'sticky', top: 0, zIndex: 5, background: '#f8fafc' }}>
                      <tr style={{ textAlign: 'left', borderBottom: `1px solid ${borderColor}` }}>
                        <th style={{ padding: '15px', color: '#475569', fontSize: '11px', fontWeight: '800' }}>LIBELLÉ INVENTAIRE</th>
                        <th style={{ padding: '15px', color: '#475569', fontSize: '11px', fontWeight: '800' }}>STATUT</th>
                        <th style={{ padding: '15px', color: '#475569', fontSize: '11px', fontWeight: '800' }}>DATE CRÉATION</th>
                        <th style={{ padding: '15px', textAlign: 'right', color: '#475569', fontSize: '11px', fontWeight: '800' }}>ACTIONS</th>
                      </tr>
                    </thead>
                    <tbody>
                      {inventairesActifs.map(inv => (
                        <tr key={inv.id} style={{ borderBottom: `1px solid ${borderColor}` }}>
                          <td style={{ padding: '15px', fontWeight: '800', color: '#0f172a' }}>{inv.libelle}</td>
                          <td style={{ padding: '15px' }}>
                            <span style={{ padding: '4px 10px', borderRadius: '6px', fontSize: '11px', fontWeight: 'bold', background: '#fef3c7', color: '#92400e' }}>
                              {inv.statut?.toUpperCase()}
                            </span>
                          </td>
                          <td style={{ padding: '15px', color: '#64748b', fontSize: '13px' }}>{new Date(inv.created_at).toLocaleDateString()}</td>
                          <td style={{ padding: '15px', textAlign: 'right', display: 'flex', gap: '8px', justifyContent: 'flex-end', alignItems: 'center' }}>
                            <button onClick={() => { fetchDetails(inv.id); setSearchCode(''); setSearchNom(''); setSearchCategorie(''); }} style={{ background: '#eff6ff', color: '#3b82f6', border: 'none', padding: '8px 14px', borderRadius: '6px', fontWeight: 'bold', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                              <Eye size={15} /> Ouvrir
                            </button>
                            <button onClick={() => supprimerInventaire(inv.id)} style={{ background: '#ffeeef', color: '#ef4444', border: 'none', padding: '8px 14px', borderRadius: '6px', fontWeight: 'bold', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                              <Trash2 size={15} /> Annuler
                            </button>
                          </td>
                        </tr>
                      ))}
                      {inventairesActifs.length === 0 && (
                        <tr><td colSpan="4" style={{ padding: '30px', textAlign: 'center', color: '#94a3b8' }}>Aucun inventaire en cours.</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}

              {mainTab === 'historique' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '20px', overflow: 'hidden', flex: 1 }}>
                  <div style={{ background: 'white', borderRadius: '12px', border: `1px solid ${borderColor}`, overflowY: 'auto', maxHeight: 'calc(3 * 55px + 40px + 45px)', flexShrink: 0 }}>
                    <div style={{ padding: '15px 20px', background: '#f8fafc', borderBottom: `1px solid ${borderColor}`, fontWeight: '900', fontSize: '12px', color: '#475569', position: 'sticky', top: 0, zIndex: 5 }}>
                      📋 LISTE DES SESSIONS CLÔTURÉES (CLIQUER SUR UNE LIGNE POUR VOIR LES DÉTAILS)
                    </div>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead style={{ position: 'sticky', top: '47px', zIndex: 5 }}>
                        <tr style={{ background: primaryColor, color: 'white', textAlign: 'left' }}>
                          <th style={{ padding: '12px 15px', fontSize: '11px', fontWeight: '800' }}>RÉF / LIBellé</th>
                          <th style={{ padding: '12px 15px', fontSize: '11px', fontWeight: '800' }}>STATUT</th>
                          <th style={{ padding: '12px 15px', fontSize: '11px', fontWeight: '800' }}>DATE DE CLÔTURE</th>
                          <th style={{ padding: '12px 15px', textAlign: 'right', fontSize: '11px', fontWeight: '800' }}>VALEUR THÉORIQUE</th>
                          <th style={{ padding: '12px 15px', textAlign: 'right', fontSize: '11px', fontWeight: '800' }}>VALEUR RÉELLE</th>
                          <th style={{ padding: '12px 15px', textAlign: 'right', fontSize: '11px', fontWeight: '800' }}>ÉCART VALEUR TOTAL</th>
                          <th style={{ padding: '12px 15px', textAlign: 'right', fontSize: '11px', fontWeight: '800' }}>ACTION</th>
                        </tr>
                      </thead>
                      <tbody>
                        {inventairesHistorique.map(inv => {
                          const estSelectionne = selectedSessionId === inv.id;
                          const ecartGlobal = Number(inv.valeur_ecart_totale || 0);
                          return (
                            <tr 
                              key={inv.id} 
                              onClick={() => {
                                handleSelectSessionHistorique(inv.id);
                                setSearchCode(''); setSearchNom(''); setSearchCategorie('');
                              }}
                              style={{ borderBottom: `1px solid ${borderColor}`, cursor: 'pointer', background: estSelectionne ? '#f0fdf4' : 'white', transition: 'background 0.2s' }}
                            >
                              <td style={{ padding: '15px', fontWeight: '800', color: '#0f172a' }}>{inv.libelle}</td>
                              <td style={{ padding: '15px' }}>
                                <span style={{ padding: '4px 10px', borderRadius: '6px', fontSize: '11px', fontWeight: 'bold', background: '#d1fae5', color: '#065f46', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                                  <Lock size={12}/> CLÔTURÉ
                                </span>
                              </td>
                              <td style={{ padding: '15px', color: '#64748b', fontSize: '13px' }}>{new Date(inv.closed_at || inv.updated_at).toLocaleString()}</td>
                              
                              <td style={{ padding: '15px', textAlign: 'right', fontWeight: '700', fontSize: '13px', color: '#334155' }}>
                                {Math.round(Number(inv.valeur_theo_totale || 0)).toLocaleString()} F
                              </td>
                              <td style={{ padding: '15px', textAlign: 'right', fontWeight: '700', fontSize: '13px', color: '#059669' }}>
                                {Math.round(Number(inv.valeur_reel_totale || 0)).toLocaleString()} F
                              </td>
                              <td style={{ padding: '15px', textAlign: 'right', fontWeight: '900', fontSize: '13px', color: ecartGlobal < 0 ? '#ef4444' : (ecartGlobal > 0 ? '#10b981' : '#64748b') }}>
                                {ecartGlobal > 0 ? `+${Math.round(ecartGlobal).toLocaleString()}` : Math.round(ecartGlobal).toLocaleString()} F
                              </td>

                              <td style={{ padding: '15px', textAlign: 'right' }}>
                                <span style={{ color: primaryColor, fontSize: '12px', fontWeight: 'bold' }}>
                                  {estSelectionne ? 'Masquer détails ▲' : 'Afficher détails ▼'}
                                </span>
                              </td>
                            </tr>
                          );
                        })}
                        {inventairesHistorique.length === 0 && (
                          <tr><td colSpan="7" style={{ padding: '30px', textAlign: 'center', color: '#94a3b8' }}>Aucun inventaire dans l'historique.</td></tr>
                        )}
                      </tbody>
                    </table>
                  </div>

                  {selectedSessionId && (
                    <div style={{ background: 'white', borderRadius: '12px', border: `1px solid ${borderColor}`, padding: '15px', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)', display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px', borderBottom: `1px solid ${borderColor}`, paddingBottom: '8px', flexShrink: 0 }}>
                        <h3 style={{ margin: 0, fontSize: '14px', fontWeight: '900', color: '#0f172a' }}>
                          Détails des écarts de la session : {selectedSessionId}
                        </h3>
                        <button onClick={() => setSelectedSessionId(null)} style={{ background: '#f1f5f9', border: 'none', padding: '6px 12px', borderRadius: '6px', cursor: 'pointer', fontSize: '11px', fontWeight: 'bold', color: '#475569' }}>
                          Fermer
                        </button>
                      </div>

                      <div style={{ display: 'flex', gap: '10px', marginBottom: '10px', flexShrink: 0 }}>
                        <div style={{ background: '#f8fafc', border: `1px solid ${borderColor}`, borderRadius: '10px', display: 'flex', alignItems: 'center', padding: '0 12px', flex: 1 }}>
                          <Barcode size={18} color="#64748b"/>
                          <input 
                            placeholder="Filtrer par code..." 
                            style={{ border: 'none', padding: '8px', width: '100%', outline: 'none', background: 'transparent', fontSize: '13px', fontWeight: '600' }} 
                            value={searchCode} 
                            onChange={(e) => setSearchCode(e.target.value)} 
                          />
                        </div>
                        <div style={{ background: '#f8fafc', border: `1px solid ${borderColor}`, borderRadius: '10px', display: 'flex', alignItems: 'center', padding: '0 12px', flex: 1 }}>
                          <Search size={18} color="#64748b"/>
                          <input 
                            placeholder="Filtrer par nom..." 
                            style={{ border: 'none', padding: '8px', width: '100%', outline: 'none', background: 'transparent', fontSize: '13px', fontWeight: '600' }} 
                            value={searchNom} 
                            onChange={(e) => setSearchNom(e.target.value)} 
                          />
                        </div>
                        <div style={{ background: '#f8fafc', border: `1px solid ${borderColor}`, borderRadius: '10px', display: 'flex', alignItems: 'center', padding: '0 12px', flex: 1 }}>
                          <Search size={18} color="#64748b"/>
                          <input 
                            placeholder="Filtrer par catégorie..." 
                            style={{ border: 'none', padding: '8px', width: '100%', outline: 'none', background: 'transparent', fontSize: '13px', fontWeight: '600' }} 
                            value={searchCategorie} 
                            onChange={(e) => setSearchCategorie(e.target.value)} 
                          />
                        </div>
                      </div>

                      <div style={{ overflowY: 'auto', maxHeight: 'calc(9 * 42px + 40px)', border: `1px solid ${borderColor}`, borderRadius: '8px' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                          <thead style={{ position: 'sticky', top: 0, zIndex: 5, background: '#f8fafc' }}>
                            <tr style={{ textAlign: 'left', borderBottom: `1px solid ${borderColor}` }}>
                              <th onClick={() => handleSort('nom')} style={{ padding: '10px', fontSize: '11px', fontWeight: '800', color: '#475569', cursor: 'pointer' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>MATIÈRE PREMIÈRE <ArrowUpDown size={14}/></div>
                              </th>
                              <th onClick={() => handleSort('categorie')} style={{ padding: '10px', fontSize: '11px', fontWeight: '800', color: '#475569', cursor: 'pointer' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>CATÉGORIE <ArrowUpDown size={14}/></div>
                              </th>
                              <th onClick={() => handleSort('stock_theorique')} style={{ padding: '10px', fontSize: '11px', fontWeight: '800', color: '#475569', textAlign: 'center', cursor: 'pointer' }}>
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '5px' }}>STOCK THÉORIQUE <ArrowUpDown size={14}/></div>
                              </th>
                              <th onClick={() => handleSort('stock_reel')} style={{ padding: '10px', fontSize: '11px', fontWeight: '800', color: '#475569', textAlign: 'center', cursor: 'pointer' }}>
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '5px' }}>STOCK RÉEL <ArrowUpDown size={14}/></div>
                              </th>
                              <th style={{ padding: '10px', fontSize: '11px', fontWeight: '800', color: '#475569', textAlign: 'center' }}>ÉCART QTÉ</th>
                              <th style={{ padding: '10px', fontSize: '11px', fontWeight: '800', color: '#475569', textAlign: 'center' }}>CMP / PRIX</th>
                              <th style={{ padding: '10px', fontSize: '11px', fontWeight: '800', color: '#475569', textAlign: 'center' }}>ÉCART VALEUR</th>
                            </tr>
                          </thead>
                          <tbody>
                            {sessionDetailsFiltres.map(item => {
                              const matRef = allMatieres.find(m => m.id === item.matiere_id);
                              const categorieArt = matRef?.categorie || matRef?.categorie_nom || matRef?.category || '---';
                              const uniteMesure = item.unite_mesure || matRef?.unite_mesure || '';

                              const ecartQte = Number(item.stock_reel) - Number(item.stock_theorique);
                              const ecartVal = ecartQte * Number(item.prix_achat_snap || 0);
                              return (
                                <tr key={item.id} style={{ borderBottom: `1px solid ${borderColor}` }}>
                                  <td style={{ padding: '10px', fontSize: '12px', fontWeight: '800', color: '#0f172a' }}>{item.nom_matiere_snap}</td>
                                  <td style={{ padding: '10px', fontSize: '12px', fontWeight: '600', color: '#475569' }}>{categorieArt}</td>
                                  <td style={{ padding: '10px', fontSize: '12px', textAlign: 'center', color: '#64748b' }}>{Number(item.stock_theorique).toFixed(3)} {uniteMesure}</td>
                                  <td style={{ padding: '10px', fontSize: '12px', textAlign: 'center', fontWeight: 'bold', color: primaryColor }}>{Number(item.stock_reel).toFixed(3)} {uniteMesure}</td>
                                  <td style={{ padding: '10px', fontSize: '12px', textAlign: 'center', fontWeight: 'bold', color: ecartQte < 0 ? '#ef4444' : (ecartQte > 0 ? '#10b981' : '#64748b') }}>
                                    {ecartQte > 0 ? `+${ecartQte.toFixed(3)}` : ecartQte.toFixed(3)} {uniteMesure}
                                  </td>
                                  <td style={{ padding: '10px', fontSize: '12px', textAlign: 'center' }}>{Number(item.prix_achat_snap).toLocaleString()}</td>
                                  <td style={{ padding: '10px', fontSize: '12px', textAlign: 'center', fontWeight: 'bold', color: ecartVal < 0 ? '#ef4444' : (ecartVal > 0 ? '#10b981' : '#64748b') }}>
                                    {Math.round(ecartVal).toLocaleString()}
                                  </td>
                                </tr>
                              );
                            })}
                            {sessionDetailsFiltres.length === 0 && (
                              <tr><td colSpan="7" style={{ padding: '20px', textAlign: 'center', color: '#94a3b8' }}>Aucun détail trouvé.</td></tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              )}

            </div>
          ) : (
            <div style={{ display: 'flex', gap: '20px', flex: 1, overflow: 'hidden', height: '100%' }}>
              
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', background: 'white', borderRadius: '16px', border: `1px solid ${borderColor}`, padding: '20px', overflow: 'hidden' }}>
                
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px', borderBottom: `1px solid ${borderColor}`, paddingBottom: '12px', flexShrink: 0 }}>
                  <div>
                    <button onClick={() => { setSelectedInventaire(null); setItems([]); setArticleEnCours(null); }} style={{ background: '#f1f5f9', border: 'none', padding: '6px 12px', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold', marginBottom: '6px', color: '#475569' }}>
                      ← Retour à la liste
                    </button>
                    <h2 style={{ margin: 0, fontSize: '16px', fontWeight: '900', color: '#0f172a' }}>{selectedInventaire.libelle}</h2>
                  </div>
                  
                  <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                    {selectedInventaire.statut !== 'valide' && (
                      <>
                        <button onClick={mettreAZeroLeReste} style={{ background: '#475569', color: 'white', border: 'none', padding: '8px 14px', borderRadius: '8px', fontWeight: 'bold', cursor: 'pointer', fontSize: '12px' }}>
                          RESTE À 0
                        </button>
                        <button onClick={() => handleValidateInventaire(selectedInventaire.id)} style={{ background: primaryColor, color: 'white', border: 'none', padding: '8px 16px', borderRadius: '8px', fontWeight: 'bold', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px' }}>
                          <CheckCircle size={16} /> CLÔTURER
                        </button>
                      </>
                    )}
                    {selectedInventaire.statut === 'valide' && (
                      <span style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#065f46', fontWeight: 'bold', background: '#d1fae5', padding: '8px 15px', borderRadius: '8px', fontSize: '12px' }}>
                        <Lock size={16} /> Inventaire Clôturé et Validé
                      </span>
                    )}
                  </div>
                </div>

                <div style={{ display: 'flex', gap: '10px', marginBottom: '15px', flexShrink: 0 }}>
                  <div style={{ background: '#f8fafc', border: `1px solid ${borderColor}`, borderRadius: '10px', display: 'flex', alignItems: 'center', padding: '0 12px', flex: 1 }}>
                    <Barcode size={18} color="#64748b"/>
                    <input 
                      placeholder="Filtrer par code..." 
                      style={{ border: 'none', padding: '8px', width: '100%', outline: 'none', background: 'transparent', fontSize: '13px', fontWeight: '600' }} 
                      value={searchCode} 
                      onChange={(e) => setSearchCode(e.target.value)} 
                    />
                  </div>
                  <div style={{ background: '#f8fafc', border: `1px solid ${borderColor}`, borderRadius: '10px', display: 'flex', alignItems: 'center', padding: '0 12px', flex: 1 }}>
                    <Search size={18} color="#64748b"/>
                    <input 
                      placeholder="Filtrer par nom..." 
                      style={{ border: 'none', padding: '8px', width: '100%', outline: 'none', background: 'transparent', fontSize: '13px', fontWeight: '600' }} 
                      value={searchNom} 
                      onChange={(e) => setSearchNom(e.target.value)} 
                    />
                  </div>
                  <div style={{ background: '#f8fafc', border: `1px solid ${borderColor}`, borderRadius: '10px', display: 'flex', alignItems: 'center', padding: '0 12px', flex: 1 }}>
                    <Search size={18} color="#64748b"/>
                    <input 
                      placeholder="Filtrer par catégorie..." 
                      style={{ border: 'none', padding: '8px', width: '100%', outline: 'none', background: 'transparent', fontSize: '13px', fontWeight: '600' }} 
                      value={searchCategorie} 
                      onChange={(e) => setSearchCategorie(e.target.value)} 
                    />
                  </div>
                </div>

                <div style={{ flex: 1, overflowY: 'auto', maxHeight: 'calc(9 * 45px + 40px)', border: `1px solid ${borderColor}`, borderRadius: '10px' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead style={{ background: '#f8fafc', position: 'sticky', top: 0, zIndex: 5 }}>
                      <tr style={{ textAlign: 'left', borderBottom: `1px solid ${borderColor}` }}>
                        <th onClick={() => handleSort('id')} style={{ padding: '12px', fontSize: '11px', fontWeight: '800', color: '#475569', cursor: 'pointer' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>IDENTIFIANT <ArrowUpDown size={14}/></div>
                        </th>
                        <th onClick={() => handleSort('nom')} style={{ padding: '12px', fontSize: '11px', fontWeight: '800', color: '#475569', cursor: 'pointer' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>MATIÈRE PREMIÈRE <ArrowUpDown size={14}/></div>
                        </th>
                        <th onClick={() => handleSort('categorie')} style={{ padding: '12px', fontSize: '11px', fontWeight: '800', color: '#475569', cursor: 'pointer' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>CATÉGORIE <ArrowUpDown size={14}/></div>
                        </th>
                        <th onClick={() => handleSort('stock_theorique')} style={{ padding: '12px', fontSize: '11px', fontWeight: '800', color: '#475569', textAlign: 'center', cursor: 'pointer' }}>
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '5px' }}>STOCK THÉORIQUE <ArrowUpDown size={14}/></div>
                        </th>
                        <th onClick={() => handleSort('stock_reel')} style={{ padding: '12px', fontSize: '11px', fontWeight: '800', color: '#475569', textAlign: 'center', cursor: 'pointer' }}>
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '5px' }}>STOCK RÉEL <ArrowUpDown size={14}/></div>
                        </th>
                        <th style={{ padding: '12px', fontSize: '11px', fontWeight: '800', color: '#475569', textAlign: 'center' }}>ÉCART QTÉ</th>
                        <th style={{ padding: '12px', fontSize: '11px', fontWeight: '800', color: '#475569', textAlign: 'center' }}>PRIX ACHAT / CMP</th>
                        <th style={{ padding: '12px', fontSize: '11px', fontWeight: '800', color: '#475569', textAlign: 'center' }}>ÉCART VALEUR</th>
                      </tr>
                    </thead>
                    <tbody>
                      {itemsFiltrés.map(item => {
                        const matRef = allMatieres.find(m => m.id === item.matiere_id);
                        const codeArt = matRef?.id || item.matiere_id;
                        const categorieArt = matRef?.categorie || matRef?.categorie_nom || matRef?.category || '---';
                        const uniteMesure = item.unite_mesure || matRef?.unite_mesure || '';
                        
                        const isCompté = item.stock_reel !== undefined && item.stock_reel !== null && item.stock_reel !== '';
                        const qReel = isCompté ? Number(item.stock_reel) : '---';
                        const ecartQte = isCompté ? qReel - Number(item.stock_theorique) : 0;
                        const ecartVal = ecartQte * Number(item.prix_achat_snap || 0);

                        const isSelected = articleEnCours?.id === item.id;

                        let rowBg = 'white';
                        if (isSelected) {
                          rowBg = '#e6fffa'; 
                        } else if (isCompté) {
                          rowBg = '#f0fdf4'; 
                        }

                        return (
                          <tr 
                            key={item.id} 
                            style={{ 
                              borderBottom: `1px solid ${borderColor}`, 
                              cursor: selectedInventaire.statut !== 'valide' ? 'pointer' : 'default', 
                              background: rowBg,
                              transition: 'background 0.2s ease',
                              borderLeft: isSelected ? `4px solid ${primaryColor}` : 'none'
                            }}
                            onClick={() => selectionnerArticle(item)}
                          >
                            <td style={{ padding: '12px', fontSize: '12px', fontWeight: '700', color: '#64748b' }}>{codeArt}</td>
                            <td style={{ padding: '12px', fontSize: '13px', fontWeight: '800', color: '#0f172a' }}>{item.nom_matiere_snap}</td>
                            <td style={{ padding: '12px', fontSize: '12px', fontWeight: '600', color: '#475569' }}>{categorieArt}</td>
                            <td style={{ padding: '12px', fontSize: '13px', textAlign: 'center', fontWeight: '600', color: '#64748b' }}>{Number(item.stock_theorique).toFixed(3)} {uniteMesure}</td>
                            <td style={{ padding: '12px', fontSize: '13px', textAlign: 'center', fontWeight: '900', color: isCompté ? primaryColor : '#94a3b8' }}>
                              {isCompté ? `${qReel} ${uniteMesure}` : '---'}
                            </td>
                            <td style={{ padding: '12px', fontSize: '13px', textAlign: 'center', fontWeight: '800', color: ecartQte < 0 ? '#ef4444' : (ecartQte > 0 ? '#10b981' : '#64748b') }}>
                              {isCompté ? `${Number(ecartQte).toFixed(3)} ${uniteMesure}` : '---'}
                            </td>
                            <td style={{ padding: '12px', fontSize: '13px', textAlign: 'center', fontWeight: '600' }}>{Number(item.prix_achat_snap).toLocaleString()}</td>
                            <td style={{ padding: '12px', fontSize: '13px', textAlign: 'center', fontWeight: '800', color: ecartVal < 0 ? '#ef4444' : (ecartVal > 0 ? '#10b981' : '#64748b') }}>
                              {isCompté ? Number(ecartVal).toLocaleString() : '---'}
                            </td>
                          </tr>
                        );
                      })}
                      {itemsFiltrés.length === 0 && (
                        <tr><td colSpan="8" style={{ padding: '30px', textAlign: 'center', color: '#94a3b8' }}>Aucune matière première trouvée.</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>

                <div style={{ background: '#0f172a', padding: '12px 20px', borderRadius: '12px', marginTop: '15px', color: 'white', display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '15px', flexShrink: 0 }}>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', fontSize: '10px', color: '#94a3b8' }}>VAL. THÉORIQUE: <strong style={{ fontSize: '13px', fontWeight: '900', color: 'white', marginTop: '2px' }}>{Math.round(totaux.valTheo).toLocaleString()}</strong></div>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', fontSize: '10px', color: '#94a3b8' }}>VAL. COMPTÉE: <strong style={{ fontSize: '13px', fontWeight: '900', color: '#6ee7b7', marginTop: '2px' }}>{Math.round(totaux.valReel).toLocaleString()}</strong></div>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', fontSize: '10px', color: '#94a3b8' }}>ÉCART VALEUR: <strong style={{ fontSize: '13px', fontWeight: '900', color: totaux.ecartVal >= 0 ? '#4ade80' : '#f87171', marginTop: '2px' }}>{Math.round(totaux.ecartVal).toLocaleString()}</strong></div>
                </div>

              </div>

              <div style={{ width: '280px', display: 'flex', flexDirection: 'column', gap: '12px', flexShrink: 0 }}>
                
                {selectedInventaire.statut !== 'valide' && (
                  <button 
                    style={{ background: '#ef4444', color: 'white', border: 'none', padding: '12px', borderRadius: '12px', fontWeight: '900', fontSize: '12px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }} 
                    onClick={() => ouvrirDialogue("Abandonner ?", "Voulez-vous supprimer et abandonner cet inventaire ?", "danger", annulerEtAbandonner)}
                  >
                    ABANDONNER L'INVENTAIRE
                  </button>
                )}

                {articleEnCours && selectedInventaire.statut !== 'valide' ? (
                  <div style={{ background: 'white', border: `1px solid ${borderColor}`, borderRadius: '16px', padding: '15px', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)' }}>
                    <div style={{ background: primaryColor, color: 'white', padding: '8px', fontSize: '11px', fontWeight: '900', textAlign: 'center', borderRadius: '8px', marginBottom: '12px' }}>SAISIE QUANTITÉ RÉELLE</div>
                    <p style={{ fontWeight: '900', fontSize: '12px', marginBottom: '8px', color: '#0f172a' }}>{articleEnCours.nom_matiere_snap}</p>
                    <p style={{ fontSize: '11px', color: '#64748b', marginBottom: '10px' }}>
                      Théorique : <strong>{articleEnCours.stock_theorique} {articleEnCours.unite_mesure || allMatieres.find(m => m.id === articleEnCours.matiere_id)?.unite_mesure || ''}</strong>
                    </p>
                    
                    <input 
                      ref={inputRef} 
                      type="number" 
                      step="0.001"
                      style={{ width: '100%', padding: '10px', border: `2px solid ${primaryColor}`, borderRadius: '10px', fontSize: '16px', textAlign: 'center', marginBottom: '10px', outline: 'none', fontWeight: '900', boxSizing: 'border-box' }} 
                      value={qteSaisie} 
                      onChange={(e) => setQteSaisie(e.target.value)} 
                      onKeyDown={(e) => e.key === 'Enter' && enregistrerQte()}
                    />
                    
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button style={{ flex: 1, background: 'white', color: '#64748b', border: `1px solid ${borderColor}`, padding: '8px', borderRadius: '8px', cursor: 'pointer', fontWeight: '900', fontSize: '11px' }} onClick={() => setArticleEnCours(null)}>RETOUR</button>
                      <button style={{ flex: 1, background: primaryColor, color: 'white', border: 'none', padding: '8px', borderRadius: '8px', cursor: 'pointer', fontWeight: '900', fontSize: '11px' }} onClick={enregistrerQte}>VALIDER</button>
                    </div>
                  </div>
                ) : (
                  <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', border: '2px dashed #cbd5e1', borderRadius: '16px', padding: '20px', background: 'white' }}>
                    <Info size={30} color="#94a3b8" />
                    <p style={{ fontSize: '11px', marginTop: '10px', color: '#64748b', fontWeight: '800' }}>
                      CLIQUEZ SUR UNE LIGNE DU TABLEAU POUR SAISIR SON STOCK RÉEL
                    </p>
                  </div>
                )}

              </div>

            </div>
          )}
        </div>
      </main>

      {isModalOpen && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: 'white', padding: '30px', borderRadius: '16px', width: '450px', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)' }}>
            <h3 style={{ margin: '0 0 15px 0', fontSize: '16px', fontWeight: '900' }}>Nouvel Inventaire Matières Premières</h3>
            <form onSubmit={handleCreateInventaire}>
              <div style={{ marginBottom: '20px' }}>
                <label style={{ fontSize: '11px', fontWeight: '800', color: '#64748b', display: 'block', marginBottom: '6px' }}>LIBELLÉ / CODE INVENTAIRE</label>
                <input required style={{ padding: '10px 14px', borderRadius: '8px', border: `1px solid ${borderColor}`, fontSize: '14px', width: '100%', background: 'white', boxSizing: 'border-box' }} value={libelle} onChange={e => setLibelle(e.target.value)} />
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                <button type="button" onClick={() => setIsModalOpen(false)} style={{ background: '#f1f5f9', border: 'none', padding: '10px 16px', borderRadius: '8px', fontWeight: 'bold', cursor: 'pointer' }}>Annuler</button>
                <button type="submit" style={{ background: primaryColor, color: 'white', border: 'none', padding: '10px 20px', borderRadius: '8px', fontWeight: 'bold', cursor: 'pointer' }}>Générer l'inventaire</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {dialogue.show && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(15,23,42,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100 }}>
          <div style={{ background: 'white', borderRadius: '16px', width: '350px', overflow: 'hidden' }}>
            <div style={{ background: '#f8fafc', padding: '15px', borderBottom: `1px solid ${borderColor}`, display: 'flex', alignItems: 'center' }}>
              <ShieldAlert size={20} color={dialogue.type === 'danger' ? '#dc2626' : primaryColor} /> 
              <span style={{ fontWeight: '900', paddingLeft: '8px' }}>{dialogue.titre}</span>
            </div>
            <div style={{ padding: '20px', textAlign: 'center', fontWeight: '700', fontSize: '13px', color: '#334155' }}>{dialogue.msg}</div>
            <div style={{ padding: '15px', background: '#f8fafc', borderTop: `1px solid ${borderColor}`, display: 'flex', gap: '10px' }}>
              <button style={{ flex: 1, background: 'white', color: '#64748b', border: `1px solid ${borderColor}`, padding: '10px', borderRadius: '8px', cursor: 'pointer', fontWeight: '900' }} onClick={fermerDialogue}>Annuler</button>
              <button 
                style={{ flex: 1, background: dialogue.type === 'danger' ? '#dc2626' : primaryColor, color: 'white', border: 'none', padding: '10px', borderRadius: '8px', cursor: 'pointer', fontWeight: '900' }} 
                onClick={() => { dialogue.action(); fermerDialogue(); }}
              >
                Confirmer
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default InventaireMp;